<template>
  <div class="timeline-wrapper" style="width: 95%">
    <div class="timeline-row">
      <div v-for="(block, i) in computedBlocks" :key="i" class="period-block" :style="{ left: block.leftPct + '%', width: block.widthPct + '%', background: block.color }" @mousemove="onMouseMove($event, block)" @mouseleave="tooltipVisible = false">
        <span v-if="block.widthPct > 4">{{ block.days }}d</span>
      </div>
    </div>

    <div class="timeline-axis">
      <div v-for="tick in axisTicks" :key="tick.day" class="axis-tick" :style="{ left: tick.pct + '%' }">{{ tick.label }}</div>
    </div>

    <div class="timeline-legend">
      <div v-for="(color, id) in companyColors" :key="id" class="legend-item">
        <span class="legend-dot" :style="{ background: color }"></span>
        {{ $t("labels.client") }} {{ company_by_id(id).company_name }} &mdash; {{ companyTotalDays[id] }} {{ $t("labels.days") }}
      </div>
    </div>

    <div v-if="tooltipVisible" class="period-tooltip" :style="{ right: tooltipX + 'px', top: tooltipY + 'px' }">
      <strong>{{ $t("labels.client") }} {{ company_by_id(tooltipData.companyId).company_name }}</strong
      ><br />
      {{ tooltipData.start }} &rarr; {{ tooltipData.end }}<br />
      <small>Day {{ tooltipData.startDay }}–{{ tooltipData.endDay }} ({{ tooltipData.days }} days)</small>
    </div>
  </div>
</template>

<script>
const PALETTE = ["#3b82f6", "#10b981", "#f59e0b", "#ef4444", "#8b5cf6", "#ec4899", "#06b6d4"];

export default {
  name: "WorkperiodTimeline",

  props: {
    // Array of { startdate: 'YYYY-MM-DD', enddate: 'YYYY-MM-DD', company_id: Number, ... }
    periods: {
      type: Array,
      required: true,
    },
    totalDays: {
      type: Number,
      default: 365,
    },
  },

  data() {
    return {
      tooltipVisible: false,
      tooltipX: 0,
      tooltipY: 0,
      tooltipData: {},
    };
  },

  computed: {
    day0() {
      const d = new Date();
      d.setHours(0, 0, 0, 0);
      d.setFullYear(d.getFullYear() - 1);
      return d;
    },

    companyColors() {
      const map = {};
      let idx = 0;
      this.periods.forEach((p) => {
        if (!map[p.company_id]) map[p.company_id] = PALETTE[idx++ % PALETTE.length];
      });
      return map;
    },

    companyTotalDays() {
      const totals = {};
      this.periods.forEach((p) => {
        const days = this.daysBetween(this.parseDate(p.startdate), this.parseDate(p.enddate));
        totals[p.company_id] = (totals[p.company_id] || 0) + days;
      });
      return totals;
    },

    computedBlocks() {
      return this.periods.map((p) => {
        const today = new Date(); today.setHours(0, 0, 0, 0);
        const originalStart = this.parseDate(p.startdate);
        const start = originalStart < this.day0 ? this.day0 : originalStart;
        const originalEnd = this.parseDate(p.enddate);
        const end = originalEnd > today ? today : originalEnd;
        const startDay = this.daysBetween(this.day0, start);
        const days = this.daysBetween(start, end);
        return {
          leftPct: (startDay / this.totalDays) * 100,
          widthPct: (days / this.totalDays) * 100,
          color: this.companyColors[p.company_id],
          companyId: p.company_id,
          startDay,
          endDay: startDay + days,
          days,
          start: this.formatDate(p.startdate),
          end: this.formatDate(p.enddate),
        };
      });
    },

    axisTicks() {
      const ticks = [];
      for (let i = 0; i <= 13; i++) {
        const day = Math.round((i / 13) * this.totalDays);
        const date = new Date(this.day0.getTime() + day * 86400000);
        ticks.push({
          day,
          pct: ((day / this.totalDays) * 100).toFixed(2),
          label: date.toLocaleDateString("nl-NL", { day: "numeric", month: "short" }),
        });
      }
      return ticks;
    },
  },

  methods: {
    parseDate(str) {
      const [y, m, d] = str.split("-").map(Number);
      return new Date(y, m - 1, d);
    },

    daysBetween(a, b) {
      return Math.round((b - a) / 86400000);
    },

    formatDate(str) {
      return this.parseDate(str).toLocaleDateString("nl-NL", { day: "numeric", month: "short", year: "numeric" });
    },

    onMouseMove(e, block) {
      const rect = this.$el.getBoundingClientRect();
      this.tooltipX = rect.right - e.clientX + 12;
      this.tooltipY = e.clientY - rect.top - 10;
      this.tooltipData = block;
      this.tooltipVisible = true;
    },
  },
};
</script>

<style scoped>
.timeline-wrapper {
  position: relative;
}

.timeline-row {
  position: relative;
  height: 36px;
  background: #e9ecef;
  border-radius: 4px;
}

.period-block {
  position: absolute;
  top: 0;
  height: 100%;
  border-radius: 3px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 11px;
  font-weight: 600;
  color: #fff;
  cursor: default;
  overflow: hidden;
  white-space: nowrap;
  box-sizing: border-box;
  border: 1px solid rgba(0, 0, 0, 0.15);
  transition: filter 0.15s;
}
.period-block:hover {
  filter: brightness(1.15);
  z-index: 10;
}

.timeline-axis {
  position: relative;
  height: 20px;
  margin-top: 2px;
}
.axis-tick {
  position: absolute;
  transform: translateX(-50%);
  font-size: 10px;
  color: #888;
  white-space: nowrap;
}

.timeline-legend {
  display: flex;
  gap: 16px;
  margin-top: 12px;
  flex-wrap: wrap;
}
.legend-item {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 12px;
  color: #555;
}
.legend-dot {
  display: inline-block;
  width: 12px;
  height: 12px;
  border-radius: 2px;
}

.period-tooltip {
  position: absolute;
  background: rgba(0, 0, 0, 0.85);
  color: #fff;
  padding: 6px 10px;
  border-radius: 4px;
  font-size: 12px;
  pointer-events: none;
  z-index: 100;
  line-height: 1.6;
  white-space: nowrap;
}
</style>
